import classNames from 'classnames'
import PT from 'prop-types'
import React, { useState } from 'react'
import DashboardTabNew from './DashboardTabNew'
import styled from 'styled-components'
import { LayoutTabsPropType } from 'index.pt'
import DashboardTabRename from './DashboardTabRename'
import { DashboardTabsProps, LayoutTab } from 'index.d'
import { Button, Tabs } from '@navikt/ds-react'
import { AddCircle, ErrorFilled, BackFilled, NextFilled, Edit } from '@navikt/ds-icons'

const DashboardTabsDiv = styled.div`
  display: flex;
  align-items: center;
 
  .nav-frontend-tabs__tab-inner {
     color: var(--navds-semantic-color-interaction-primary);
  }
  .nav-frontend-tabs__tab-inner--aktiv {
    background: var(--navds-semantic-color-component-background-alternate);
    border-bottom: 1px solid var(--navds-semantic-color-component-background-alternate) !important;
    margin-bottom: -2px;
  }
  &.editMode {
     border-bottom: 1px dashed var(--navds-semantic-color-interaction-primary);
    .nav-frontend-tabs__tab-inner--aktiv {
      color: var(--navds-color-text-primary);
      background: var(--navds-semantic-color-canvas-background);
      border: 1px dashed var(--navds-semantic-color-interaction-primary) !important;
      border-bottom: 1px solid var(--navds-semantic-color-canvas-background) !important;
    }
  }
`
const EditMode = styled.div` 
  display: flex;
  align-items: center;
`
const MarginLeftDiv = styled.div`
  margin-left: 0.5rem;
`
const MarginRightDiv = styled.div`
  margin-right: 0.5rem;
`
const TabLabelSpan = styled.span`
  cursor: text;
`

const DashboardTabs: React.FC<DashboardTabsProps> = ({
  currentTab,
  editMode,
  layouts,
  labels,
  onTabChange,
  onTabAdd,
  onTabRename,
  onTabMove,
  onTabDelete
}: DashboardTabsProps): JSX.Element => {
  const [seeNewTab, setSeeNewTab] = useState<boolean>(false)
  const [tabRenameKey, setTabRenameKey] = useState<string | undefined>(undefined)

  const onDeleteTabHandler = (key: string): void => {
    if (window.confirm('delete ' + key + '?')) {
      onTabDelete(key)
    }
  }

  const handleAcceptNewTab = (newTabLabel: string): void => {
    if (newTabLabel) {
      onTabAdd(newTabLabel)
    }
    setSeeNewTab(false)
  }

  const handleRejectNewTab = (): void => {
    setSeeNewTab(false)
  }

  const handleAcceptRenameTab = (newLabel: string): void => {
    onTabRename(tabRenameKey!, newLabel)
    setTabRenameKey(undefined)
  }

  const handleRejectRenameTab = (): void => {
    setTabRenameKey(undefined)
  }

  if (!editMode && layouts.length <= 1) {
    return <div />
  }

  return (
    <DashboardTabsDiv className={classNames({ editMode: editMode })}>
      <Tabs
        size='medium'
        defaultValue={currentTab}
        onChange={(e: string) => {
          if (e !== 'new') {
            onTabChange(e)
          }
        }}
      >
      <Tabs.List>
        {layouts.map((layoutTab: LayoutTab, index: number) => (
          <Tabs.Tab
            value={layoutTab.key}
            label={(
              <>
                {editMode
                  ? tabRenameKey === layoutTab.key
                    ? (
                      <DashboardTabRename
                        initialValue={layoutTab.label}
                        handleAcceptRenameTab={handleAcceptRenameTab}
                        handleRejectRenameTab={handleRejectRenameTab}
                      />
                    )
                    : (
                        <EditMode>
                          {index > 0 && (
                            <MarginRightDiv>
                              <Button
                                size='small'
                                variant='tertiary'
                                title={labels.modeTabToLeft}
                                onClick={(e: React.MouseEvent) => {
                                  e.preventDefault()
                                  e.stopPropagation()
                                  onTabMove(layoutTab.key, -1)
                                }}
                              >
                                <BackFilled />
                              </Button>
                            </MarginRightDiv>
                          )}
                          <TabLabelSpan title={labels.editTabTitle}>
                            {layoutTab.label}
                          </TabLabelSpan>
                          <Button
                            size='small'
                            variant='tertiary'
                            title={labels.editTab}
                            onClick={(e: any) => {
                              e.preventDefault()
                              e.stopPropagation()
                              setTabRenameKey(layoutTab.key)
                            }}
                            >
                          <Edit/>
                          </Button>
                          {layouts.length > 1 && (
                            <MarginLeftDiv>
                              <Button
                                size='small'
                                variant='tertiary'
                                title={labels.deleteTab}>
                                <ErrorFilled
                                  color='red'
                                  onClick={(e: any) => {
                                    e.preventDefault()
                                    e.stopPropagation()
                                    onDeleteTabHandler(layoutTab.key)
                                  }}
                                />
                              </Button>
                            </MarginLeftDiv>
                          )}
                          <div />
                          {index < layouts.length - 1 && (
                            <MarginLeftDiv>
                              <Button
                                size='small'
                                variant='tertiary'
                                title={labels.modeTabToRight}
                                onClick={(e) => {
                                  e.preventDefault()
                                  e.stopPropagation()
                                  onTabMove(layoutTab.key, 1)
                                }}
                              >
                                <NextFilled />
                              </Button>
                            </MarginLeftDiv>
                          )}
                        </EditMode>
                      )
                    : (
                        <span>{layoutTab.label}</span>
                    )
                  }
              </>
            )}
          />
        ))}
      {editMode
        ? (
            seeNewTab
              ? (
                <DashboardTabNew
                  handleAcceptNewTab={handleAcceptNewTab}
                  handleRejectNewTab={handleRejectNewTab}
                />
                )
              : (
                <Tabs.Tab value='new' label={(
                  <Button
                    variant='secondary'
                    size='small'
                    onClick={(e) => {
                      e.preventDefault()
                      e.stopPropagation()
                      setSeeNewTab(true)
                      return false
                    }}
                  >
                    <AddCircle
                      width={24}
                    />
                    <span>
                      {labels.addNewTab}
                    </span>
                  </Button>
                  )}/>
          )
        ): null}
      </Tabs.List>
      </Tabs>
    </DashboardTabsDiv>
  )
}

DashboardTabs.propTypes = {
  currentTab: PT.string.isRequired,
  editMode: PT.bool.isRequired,
  layouts: LayoutTabsPropType.isRequired,
  onTabAdd: PT.func.isRequired,
  onTabChange: PT.func.isRequired,
  onTabDelete: PT.func.isRequired,
  onTabMove: PT.func.isRequired,
  onTabRename: PT.func.isRequired
}
export default DashboardTabs
